/**
 * Query operations for RFP and coverage data
 *
 * Low-level API client functions for data fetching (read operations).
 * These functions handle the actual HTTP requests.
 * They are consumed by React Query hooks in queries.ts.
 *
 * NOTE: All functions require an axios instance (real or mock).
 * For testing, use createMockApiClient from @platform/api.
 */

import { logger } from "@platform/browser-logger";
import { AxiosInstance, AxiosError } from "axios";

import { injectCommissionsIntoCoverage } from "./commissionInjection";
import { mergeCoverageWithSkeleton, mergeRfpWithSkeleton } from "./skeletons";
import {
  LoadCoverageParams,
  RfpData,
  RfpResponse,
  RfpCoverage,
  RfpNotePayload,
  AnalystNotePayload,
} from "./types";

/**
 * Loads complete RFP data by ID
 *
 * Uses BTX endpoint: GET /api/btx/v1/rfps/:id
 *
 * @param rfpId - The RFP ID to load
 * @param axiosInstance - Optional axios instance for real API calls
 * @param includes - Optional comma-separated list of resources to include (default: "eligibility_classes")
 * @returns Promise resolving to RFP data
 *
 * @example
 * ```ts
 * const rfp = await loadRfp("rfp-123", axiosInstance);
 * logger.info(rfp.coverages);
 * ```
 */
export const loadRfp = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
  includes: string = "eligibility_classes",
): Promise<RfpData> => {
  const response = await axiosInstance.get<RfpResponse>(
    `btx/v1/rfps/${rfpId}?include=${includes}`,
  );

  const merged = mergeRfpWithSkeleton(response.data);

  // Inject commission data from broker.commissions[] into benefit plans
  const brokerCommissions = response.data.broker?.commissions;
  if (brokerCommissions && brokerCommissions.length > 0) {
    merged.coverages = merged.coverages.map((coverage) =>
      injectCommissionsIntoCoverage(coverage, brokerCommissions),
    );
  }

  // Surface commission parse errors so the UI can show a warning icon
  const parseErrors = response.data.broker?.parseErrors;
  if (parseErrors && parseErrors.length > 0) {
    merged.broker = { ...merged.broker, parseErrors };
  }

  return merged;
};

/**
 * Loads coverage data by type
 *
 * Note: The BTX backend doesn't have a coverage-specific endpoint.
 * Coverage data is returned as part of the RFP response.
 * This function extracts the requested coverage from the full RFP.
 *
 * @param params - Load coverage parameters
 * @param axiosInstance - Optional axios instance for real API calls
 * @returns Promise resolving to coverage data or null
 *
 * @example
 * ```ts
 * const coverage = await loadCoverageByType({
 *   coverageType: "dental",
 *   rfpId: "rfp-123"
 * }, axiosInstance);
 * ```
 */
export const loadCoverageByType = async (
  params: LoadCoverageParams,
  axiosInstance: AxiosInstance,
): Promise<RfpCoverage | null> => {
  const { rfpId, coverageType } = params;

  if (!rfpId) {
    logger.warn("[loadCoverageByType] No rfpId provided");
    return null;
  }

  // Load full RFP (already normalized via mergeRfpWithSkeleton in loadRfp)
  const rfp = await loadRfp(rfpId, axiosInstance);

  // Find the requested coverage type
  const coverage = rfp.coverages.find((c) => c.type === coverageType);

  if (!coverage) {
    logger.warn("[loadCoverageByType] Coverage not found:", { coverageType });
    return null;
  }

  // Additional normalization if needed (loadRfp already normalizes the entire RFP)
  return mergeCoverageWithSkeleton(coverage);
};

/**
 * Gets the note for an RFP
 *
 * Uses BTX endpoint: GET /btx/v1/rfps/{rfpId}/note
 *
 * @param rfpId - The RFP ID to fetch a note for
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to RFP note data
 *
 * @example
 * ```ts
 * const note = await getRfpNote("rfp-123", axiosInstance);
 * console.log(note.content);
 * ```
 */
export const getRfpNote = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpNotePayload> => {
  logger.debug("[BTX API] Getting RFP note:", { rfpId });
  try {
    const response = await axiosInstance.get<RfpNotePayload>(
      `/btx/v1/rfps/${rfpId}/note`,
    );

    logger.debug("[BTX API] Got RFP note:", { rfpId });

    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      logger.debug("[BTX API] Note not found, returning empty note:", {
        rfpId,
      });
      return { content: "" } as RfpNotePayload;
    }
    throw error;
  }
};

/**
 * Gets the analyst note for an RFP
 *
 * Uses BTX endpoint: GET /btx/v1/rfps/{rfpId}/analyst-note
 *
 * @param rfpId - The RFP ID to fetch an analyst note for
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to analyst note data
 *
 * @example
 * ```ts
 * const note = await getAnalystNote("rfp-123", axiosInstance);
 * console.log(note.content);
 * ```
 */
export const getAnalystNote = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<AnalystNotePayload> => {
  logger.debug("[BTX API] Getting analyst note:", { rfpId });
  try {
    const response = await axiosInstance.get<AnalystNotePayload>(
      `/btx/v1/rfps/${rfpId}/analyst-note`,
    );

    logger.debug("[BTX API] Got analyst note:", { rfpId });

    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      logger.debug("[BTX API] Analyst note not found, returning empty note:", {
        rfpId,
      });
      return { content: "" } as AnalystNotePayload;
    }
    throw error;
  }
};

/**
 * Gets the note for a coverage
 *
 * Uses BTX endpoint: GET /btx/v1/rfps/{rfpId}/coverages/{coverageId}/note
 *
 * @param rfpId - The RFP ID
 * @param coverageId - The coverage ID to fetch a note for
 * @param axiosInstance - Axios instance for API calls
 * @param benefitPlanType - Optional benefit plan type (e.g., "ppo", "hmo") for coverage type disambiguation
 * @returns Promise resolving to coverage note data
 *
 * @example
 * ```ts
 * const note = await getCoverageNote("rfp-123", "cov-456", axiosInstance, "ppo");
 * console.log(note.content);
 * ```
 */
export const getCoverageNote = async (
  rfpId: string,
  coverageId: string,
  axiosInstance: AxiosInstance,
  benefitPlanType?: string,
): Promise<RfpNotePayload> => {
  try {
    const params = benefitPlanType
      ? `?benefitPlanType=${encodeURIComponent(benefitPlanType)}`
      : "";
    const response = await axiosInstance.get<RfpNotePayload>(
      `/btx/v1/rfps/${rfpId}/coverages/${coverageId}/note${params}`,
    );

    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return { content: "" } as RfpNotePayload;
    }
    throw error;
  }
};

export const downloadDocumentFile = async (
  downloadLink: string,
  axiosInstance: AxiosInstance,
): Promise<Blob> => {
  const response = await axiosInstance.post<Blob>(
    "brokerportal/v1/getRfpFile",
    { downloadLink },
    { responseType: "blob" },
  );

  return response.data;
};
